import { createContext, useEffect, useRef, useState } from 'react';
import { getSession, useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';

import { WORDPRESS_MUTATION, WORDPRESS_QUERY } from '@/lib/graphql/enums';
import { useLazyQuery, useMutation, useToastDispatch } from '@/lib/hooks';
import { DEFAULT_TOAST_OPTIONS_WARNING } from './ToastProvider';

import type { ReactNode } from 'react';

const LoginModal = dynamic(() => import('@/organisms/modals/LoginModal'), {
  ssr: false,
});

const Modal = dynamic(() => import('@/molecules/modal'), {
  ssr: false,
});

export type VoteData = {
  campaign: number;
  category: number;
  seller: number;
};

type VotosRegistrados = {
  votosRegistrados: Array<VoteData>;
};

type MutationData = {
  votarSeller: {
    message: string;
    success: boolean;
  };
};

type MutationVariables = {
  campaign_id: number;
  category: number;
  seller_id: number;
};

type VotesProvider = [
  (variables: VoteData) => Promise<any>,
  {
    loading: boolean;
    isCategoryVoted: ({ category }: Pick<VoteData, 'category'>) => boolean;
    isSellerVoted: ({
      category,
      seller,
    }: Pick<VoteData, 'category' | 'seller'>) => boolean;
  },
];

export const VoteContext = createContext<VotesProvider | null>(null);

/**
 * VoteProvider
 */
export const VoteProvider = ({ children }: { children: ReactNode }) => {
  const [userVotes, setUserVotes] = useState<Array<VoteData>>([]);
  const [showModal, setShowModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToastDispatch();
  const { status } = useSession();
  const [votarSeller] = useMutation<MutationData, MutationVariables>(
    WORDPRESS_MUTATION.VOTE_SELLER,
  );

  const voteData = useRef<VoteData>();

  const DEFAULT_ERROR_MESSAGE = 'Error al relizar la votación';

  const [getUserVotes] = useLazyQuery<VotosRegistrados>(
    WORDPRESS_QUERY.USER_VOTES,
    {
      onCompleted: async ({ votosRegistrados }) => {
        setUserVotes(votosRegistrados);
        setLoading(false);
      },
    },
  );

  const execute = async (variables: VoteData) => {
    const session = await getSession();
    if (!session) {
      voteData.current = { ...variables };
      setShowLoginModal(true);
      return;
    }

    setLoading(true);
    await votarSeller({
      variables: {
        campaign_id: variables.campaign,
        category: variables.category,
        seller_id: variables.seller,
      },
    })
      .then(({ data }) => {
        if (!data) return;
        if (data.votarSeller.success) {
          setUserVotes([
            ...userVotes,
            {
              ...variables,
            },
          ]);
          setShowModal(true);
        } else {
          toast(
            data.votarSeller?.message ?? DEFAULT_ERROR_MESSAGE,
            DEFAULT_TOAST_OPTIONS_WARNING,
          );
        }
      })
      .catch(() => {
        toast(DEFAULT_ERROR_MESSAGE, DEFAULT_TOAST_OPTIONS_WARNING);
      })
      .finally(() => setLoading(false));
  };

  const isCategoryVoted = ({ category }: { category: number }) =>
    Boolean(userVotes.find((vote) => vote.category === category));

  const isSellerVoted = ({
    category,
    seller,
  }: {
    category: number;
    seller: number;
  }) =>
    Boolean(
      userVotes.find(
        (vote) => vote.category === category && vote.seller === seller,
      ),
    );

  const value: VotesProvider = [
    execute,
    {
      loading,
      isCategoryVoted,
      isSellerVoted,
    },
  ];

  useEffect(() => {
    if (status === 'authenticated') {
      setLoading(true);
      getUserVotes();
    }
  }, [getUserVotes, status]);

  return (
    <>
      <VoteContext.Provider value={value}>{children}</VoteContext.Provider>

      <Modal open={showModal} setOpen={setShowModal} unmount>
        <h2 className="mb-4 md:mb-6">GRACIAS POR PARTICIPAR</h2>

        <div className="prose">
          <p>Tu voto ha sido contabilizado correctamente en esta categoría.</p>
          <p>
            Una vez finalizado el periodo de votación, se comunicarán los
            ganadores en los próximos días.
          </p>
          <p>
            ¡Recuerda votar también a artesanos de otras categorías si no lo has
            hecho!
          </p>
        </div>
      </Modal>

      <LoginModal
        show={showLoginModal}
        setLoginModalOpen={setShowLoginModal}
        onSuccess={async () => {
          setShowLoginModal(false);
          await execute(voteData.current!);
        }}
      />
    </>
  );
};

VoteProvider.displayName = 'VoteProvider';
